// ไฟล์: frontend/src/pages/teacher/Scores.js
import React, { useState, useEffect } from 'react';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import Footer from '../../components/Footer';
import api from '../../services/api';
import Swal from 'sweetalert2';
import Chart from 'chart.js/auto';
import { DEFAULT_AVATAR } from '../../assets/default_avatar';

function TeacherScores() {
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [indicators, setIndicators] = useState([]);
  const [students, setStudents] = useState([]);
  const [scores, setScores] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // State สำหรับการกรอง
  const [selectedClassId, setSelectedClassId] = useState('');
  const [selectedSubjectId, setSelectedSubjectId] = useState('');
  const [selectedIndicatorId, setSelectedIndicatorId] = useState('');
  
  // State สำหรับกราฟ
  const [scoreChart, setScoreChart] = useState(null);
  
  // State สำหรับหน้าแสดงผล
  const [formMode, setFormMode] = useState('class'); // 'class' หรือ 'student'
  const [selectedStudentId, setSelectedStudentId] = useState('');
  
  // State สำหรับการบันทึกคะแนน
  const [editingScores, setEditingScores] = useState({});
  const [editingNotes, setEditingNotes] = useState({});
  
  useEffect(() => {
    fetchClasses();
    fetchSubjects();
  }, []);
  
  // เมื่อเลือกชั้นเรียนใหม่
  useEffect(() => {
    if (selectedClassId) {
      fetchStudentsInClass(selectedClassId);
    }
  }, [selectedClassId]);
  
  // เมื่อเลือกวิชาใหม่
  useEffect(() => {
    if (selectedSubjectId) {
      fetchIndicatorsBySubject(selectedSubjectId);
    } else {
      setIndicators([]);
      setSelectedIndicatorId('');
    }
  }, [selectedSubjectId]);
  
  // เมื่อเลือกตัวชี้วัดใหม่และโหมดเป็นแบบทั้งชั้น
  useEffect(() => {
    if (formMode === 'class' && selectedClassId && selectedIndicatorId) {
      fetchScoresByClassAndIndicator();
    }
  }, [formMode, selectedClassId, selectedIndicatorId]);
  
  // เมื่อเลือกนักเรียนคนใหม่และโหมดเป็นแบบรายบุคคล
  useEffect(() => {
    if (formMode === 'student' && selectedStudentId && selectedSubjectId) {
      fetchScoresByStudentAndSubject();
    }
  }, [formMode, selectedStudentId, selectedSubjectId]);
  
  // Cleanup กราฟเมื่อ component unmount
  useEffect(() => {
    return () => {
      if (scoreChart) {
        scoreChart.destroy();
        setScoreChart(null);
      }
    };
  }, []);
  
  // ดึงข้อมูลชั้นเรียนที่ครูรับผิดชอบ
  const fetchClasses = async () => {
    try {
      setIsLoading(true);
      const response = await api.class.getMyClasses();
      const myClasses = response.data.data || [];
      setClasses(myClasses);
      
      // เลือกชั้นเรียนแรกเป็นค่าเริ่มต้น
      if (myClasses.length > 0) {
        setSelectedClassId(myClasses[0].id);
      }
    } catch (error) {
      console.error('Error fetching classes:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถโหลดข้อมูลชั้นเรียนได้',
        confirmButtonColor: '#ff6b01'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // ดึงข้อมูลวิชาทั้งหมด
  const fetchSubjects = async () => {
    try {
      const response = await api.subject.getAllSubjects();
      setSubjects(response.data.data || []);
      
      // เลือกวิชาแรกเป็นค่าเริ่มต้น
      if (response.data.data && response.data.data.length > 0) {
        setSelectedSubjectId(response.data.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching subjects:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถโหลดข้อมูลวิชาได้',
        confirmButtonColor: '#ff6b01'
      });
    }
  };
  
  // ดึงข้อมูลตัวชี้วัดตามวิชา
  const fetchIndicatorsBySubject = async (subjectId) => {
    try {
      const response = await api.indicator.getIndicatorsBySubject(subjectId);
      const subjectIndicators = response.data.data || [];
      setIndicators(subjectIndicators);
      
      // เลือกตัวชี้วัดแรกเป็นค่าเริ่มต้น
      if (subjectIndicators.length > 0) {
        setSelectedIndicatorId(subjectIndicators[0].id);
      } else {
        setSelectedIndicatorId('');
      }
    } catch (error) {
      console.error('Error fetching indicators:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถโหลดข้อมูลตัวชี้วัดได้',
        confirmButtonColor: '#ff6b01'
      });
    }
  };
  
  // ดึงข้อมูลนักเรียนในชั้น
  const fetchStudentsInClass = async (classId) => {
    try {
      const response = await api.student.getStudentsByClass(classId);
      const classStudents = response.data.data || [];
      setStudents(classStudents);
      
      // เลือกนักเรียนคนแรกเป็นค่าเริ่มต้น
      if (classStudents.length > 0) {
        setSelectedStudentId(classStudents[0].id);
      }
    } catch (error) {
      console.error('Error fetching students:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถโหลดข้อมูลนักเรียนได้',
        confirmButtonColor: '#ff6b01'
      });
    }
  };
  
  // ดึงข้อมูลคะแนนตามชั้นเรียนและตัวชี้วัด
  const fetchScoresByClassAndIndicator = async () => {
    if (!selectedClassId || !selectedIndicatorId) return;
    
    try {
      setIsLoading(true);
      const response = await api.score.getScoresByClassAndIndicator(selectedClassId, selectedIndicatorId);
      const scoreData = response.data.data?.scores || [];
      const averageData = response.data.data?.average || {};
      
      setScores(scoreData);
      
      // สร้าง state สำหรับการแก้ไขคะแนน
      const newEditingScores = {};
      const newEditingNotes = {};
      
      // เตรียมข้อมูลสำหรับนักเรียนทั้งหมดในชั้น
      students.forEach(student => {
        // หาคะแนนของนักเรียนคนนี้
        const studentScore = scoreData.find(s => s.student_id === student.id);
        
        if (studentScore) {
          newEditingScores[student.id] = studentScore.score;
          newEditingNotes[student.id] = studentScore.note || '';
        } else {
          newEditingScores[student.id] = '';
          newEditingNotes[student.id] = '';
        }
      });
      
      setEditingScores(newEditingScores);
      setEditingNotes(newEditingNotes);
      
      // สร้างกราฟคะแนน
      createScoreChart(scoreData, averageData);
    } catch (error) {
      console.error('Error fetching scores:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถโหลดข้อมูลคะแนนได้',
        confirmButtonColor: '#ff6b01'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // ดึงข้อมูลคะแนนตามนักเรียนและวิชา
  const fetchScoresByStudentAndSubject = async () => {
    if (!selectedStudentId || !selectedSubjectId) return;
    
    try {
      setIsLoading(true);
      const response = await api.score.getScoresByStudentAndSubject(selectedStudentId, selectedSubjectId);
      const scoreData = response.data.data?.scores || [];
      const averageData = response.data.data?.average || {};
      
      setScores(scoreData);
      
      // สร้าง state สำหรับการแก้ไขคะแนน
      const newEditingScores = {};
      const newEditingNotes = {};
      
      // เตรียมข้อมูลสำหรับตัวชี้วัดทั้งหมดของวิชานี้
      indicators.forEach(indicator => {
        // หาคะแนนของตัวชี้วัดนี้
        const indicatorScore = scoreData.find(s => s.indicator_id === indicator.id);
        
        if (indicatorScore) {
          newEditingScores[indicator.id] = indicatorScore.score;
          newEditingNotes[indicator.id] = indicatorScore.note || '';
        } else {
          newEditingScores[indicator.id] = '';
          newEditingNotes[indicator.id] = '';
        }
      });
      
      setEditingScores(newEditingScores);
      setEditingNotes(newEditingNotes);
      
      // สร้างกราฟคะแนน
      createStudentSubjectChart(scoreData, averageData);
    } catch (error) {
      console.error('Error fetching student scores:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถโหลดข้อมูลคะแนนได้',
        confirmButtonColor: '#ff6b01'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // สร้างกราฟคะแนนชั้นเรียน
  const createScoreChart = (scoreData, averageData) => {
    // ทำลายกราฟเก่า (ถ้ามี)
    if (scoreChart) {
      scoreChart.destroy();
    }
    
    // ถ้าไม่มีข้อมูลคะแนน หรือไม่มี canvas ให้ออกจากฟังก์ชัน
    const canvas = document.getElementById('scoreChart');
    if (!canvas || scoreData.length === 0) return;
    
    // เตรียมข้อมูลสำหรับกราฟ
    const labels = scoreData.map(s => `${s.first_name} ${s.last_name}`);
    const data = scoreData.map(s => s.score);
    const fullScore = scoreData.length > 0 ? scoreData[0].full_score : 10;
    
    // สร้างกราฟใหม่
    const newChart = new Chart(canvas, {
      type: 'bar',
      data: {
        labels: labels,
        datasets: [{
          label: 'คะแนน',
          data: data,
          backgroundColor: 'rgba(255, 107, 1, 0.7)',
          borderColor: 'rgba(255, 107, 1, 1)',
          borderWidth: 1
        }]
      },
      options: {
        scales: {
          y: {
            beginAtZero: true,
            max: fullScore,
            title: {
              display: true,
              text: 'คะแนน'
            }
          },
          x: {
            title: {
              display: true,
              text: 'นักเรียน'
            },
            ticks: {
              maxRotation: 45,
              minRotation: 45
            }
          }
        },
        plugins: {
          title: {
            display: true,
            text: 'คะแนนตามตัวชี้วัด'
          },
          annotation: {
            annotations: {
              line1: {
                type: 'line',
                yMin: averageData.average_score,
                yMax: averageData.average_score,
                borderColor: 'rgb(255, 0, 0)',
                borderWidth: 2,
                label: {
                  content: `คะแนนเฉลี่ย: ${parseFloat(averageData.average_score || 0).toFixed(2)}`,
                  enabled: true
                }
              }
            }
          }
        }
      }
    });
    
    setScoreChart(newChart);
  };
  
  // สร้างกราฟคะแนนนักเรียนรายวิชา
  const createStudentSubjectChart = (scoreData, averageData) => {
    // ทำลายกราฟเก่า (ถ้ามี)
    if (scoreChart) {
      scoreChart.destroy();
    }
    
    // ถ้าไม่มีข้อมูลคะแนน หรือไม่มี canvas ให้ออกจากฟังก์ชัน
    const canvas = document.getElementById('scoreChart');
    if (!canvas || scoreData.length === 0) return;
    
    // เตรียมข้อมูลสำหรับกราฟ
    const labels = scoreData.map(s => s.indicator_code);
    const data = scoreData.map(s => s.score);
    const fullScores = scoreData.map(s => s.full_score);
    
    // สร้างกราฟใหม่
    const newChart = new Chart(canvas, {
      type: 'bar',
      data: {
        labels: labels,
        datasets: [{
          label: 'คะแนนที่ได้',
          data: data,
          backgroundColor: 'rgba(255, 107, 1, 0.7)',
          borderColor: 'rgba(255, 107, 1, 1)',
          borderWidth: 1
        },
        {
          label: 'คะแนนเต็ม',
          data: fullScores,
          backgroundColor: 'rgba(200, 200, 200, 0.5)',
          borderColor: 'rgba(200, 200, 200, 1)',
          borderWidth: 1,
          type: 'line'
        }]
      },
      options: {
        scales: {
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: 'คะแนน'
            }
          },
          x: {
            title: {
              display: true,
              text: 'ตัวชี้วัด'
            }
          }
        },
        plugins: {
          title: {
            display: true,
            text: 'คะแนนตามตัวชี้วัดของนักเรียน'
          }
        }
      }
    });
    
    setScoreChart(newChart);
  };
  
  // เปลี่ยนโหมดการแสดงผล
  const handleChangeFormMode = (mode) => {
    setFormMode(mode);
    
    // ล้างคะแนนที่กำลังแก้ไข
    setEditingScores({});
    setEditingNotes({});
    
    // โหลดข้อมูลตามโหมดใหม่
    if (mode === 'class' && selectedClassId && selectedIndicatorId) {
      fetchScoresByClassAndIndicator();
    } else if (mode === 'student' && selectedStudentId && selectedSubjectId) {
      fetchScoresByStudentAndSubject();
    }
  };
  
  // เปลี่ยนค่าคะแนนที่กำลังแก้ไข
  const handleChangeScore = (id, value) => {
    // ตรวจสอบว่าเป็นตัวเลขและอยู่ในช่วงที่ถูกต้อง
    const selectedIndicator = indicators.find(i => i.id === parseInt(selectedIndicatorId));
    const fullScore = selectedIndicator ? selectedIndicator.full_score : 100;
    
    if (value === '') {
      setEditingScores({ ...editingScores, [id]: '' });
    } else {
      const numValue = parseFloat(value);
      if (isNaN(numValue) || numValue < 0) {
        return;
      }
      
      if (numValue > fullScore) {
        Swal.fire({
          icon: 'warning',
          title: 'คะแนนเกินคะแนนเต็ม',
          text: `คะแนนต้องไม่เกิน ${fullScore} คะแนน`,
          confirmButtonColor: '#ff6b01'
        });
        return;
      }
      
      setEditingScores({ ...editingScores, [id]: numValue });
    }
  };
  
  // เปลี่ยนหมายเหตุที่กำลังแก้ไข
  const handleChangeNote = (id, value) => {
    setEditingNotes({ ...editingNotes, [id]: value });
  };
  
  // บันทึกคะแนน
  const handleSaveScore = async (item) => {
    let student_id, indicator_id, score, note;
    
    if (formMode === 'class') {
      student_id = item.id;
      indicator_id = selectedIndicatorId;
      score = editingScores[student_id];
      note = editingNotes[student_id];
    } else {
      student_id = selectedStudentId;
      indicator_id = item.id;
      score = editingScores[indicator_id];
      note = editingNotes[indicator_id];
    }
    
    // ตรวจสอบว่ามีคะแนนหรือไม่
    if (score === '' || score === undefined) {
      Swal.fire({
        icon: 'warning',
        title: 'กรุณากรอกคะแนน',
        text: 'กรุณากรอกคะแนนก่อนบันทึก',
        confirmButtonColor: '#ff6b01'
      });
      return;
    }
    
    try {
      await api.score.recordScore({
        student_id,
        class_id: selectedClassId,
        indicator_id,
        score,
        note
      });
      
      Swal.fire({
        icon: 'success',
        title: 'บันทึกคะแนนสำเร็จ',
        text: 'บันทึกคะแนนเรียบร้อยแล้ว',
        confirmButtonColor: '#ff6b01',
        timer: 1500,
        showConfirmButton: false
      });
      
      // โหลดข้อมูลคะแนนใหม่
      if (formMode === 'class') {
        fetchScoresByClassAndIndicator();
      } else {
        fetchScoresByStudentAndSubject();
      }
    } catch (error) {
      console.error('Error saving score:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถบันทึกคะแนนได้',
        confirmButtonColor: '#ff6b01'
      });
    }
  };
  
  // บันทึกคะแนนทั้งหมด
  const handleSaveAllScores = async () => {
    try {
      setIsLoading(true);
      
      // สร้างคำขอบันทึกคะแนนทั้งหมด
      const scoreRequests = [];
      
      if (formMode === 'class') {
        // บันทึกคะแนนของนักเรียนทุกคนในชั้น
        students.forEach(student => {
          const score = editingScores[student.id];
          const note = editingNotes[student.id];
          
          if (score !== undefined && score !== '') {
            scoreRequests.push(
              api.score.recordScore({
                student_id: student.id,
                class_id: selectedClassId,
                indicator_id: selectedIndicatorId,
                score,
                note: note || ''
              })
            );
          }
        });
      } else {
        // บันทึกคะแนนของนักเรียนคนเดียวในทุกตัวชี้วัด
        indicators.forEach(indicator => {
          const score = editingScores[indicator.id];
          const note = editingNotes[indicator.id];
          
          if (score !== undefined && score !== '') {
            scoreRequests.push(
              api.score.recordScore({
                student_id: selectedStudentId,
                class_id: selectedClassId,
                indicator_id: indicator.id,
                score,
                note: note || ''
              })
            );
          }
        });
      }
      
      // บันทึกคะแนนทั้งหมดพร้อมกัน
      await Promise.all(scoreRequests);
      
      Swal.fire({
        icon: 'success',
        title: 'บันทึกคะแนนสำเร็จ',
        text: 'บันทึกคะแนนทั้งหมดเรียบร้อยแล้ว',
        confirmButtonColor: '#ff6b01'
      });
      
      // โหลดข้อมูลคะแนนใหม่
      if (formMode === 'class') {
        fetchScoresByClassAndIndicator();
      } else {
        fetchScoresByStudentAndSubject();
      }
    } catch (error) {
      console.error('Error saving all scores:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถบันทึกคะแนนทั้งหมดได้',
        confirmButtonColor: '#ff6b01'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="wrapper">
      <Header />
      <Sidebar />
      
      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1 className="m-0">บันทึกคะแนนเก็บรายตัวชี้วัด</h1>
              </div>
              <div className="col-sm-6">
                <ol className="breadcrumb float-sm-right">
                  <li className="breadcrumb-item">
                    <a href="/teacher/dashboard">หน้าหลัก</a>
                  </li>
                  <li className="breadcrumb-item active">คะแนนเก็บ</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
        
        <section className="content">
          <div className="container-fluid">
            {isLoading ? (
              <div className="loading-spinner">
                <i className="fas fa-spinner fa-spin"></i>
              </div>
            ) : (
              <>
                {/* ตัวเลือกการบันทึกคะแนน */}
                <div className="card card-orange">
                  <div className="card-header">
                    <h3 className="card-title">ตัวเลือกการบันทึกคะแนน</h3>
                  </div>
                  <div className="card-body">
                    {classes.length === 0 ? (
                      <div className="alert alert-warning">
                        <i className="fas fa-exclamation-triangle mr-2"></i>
                        คุณยังไม่ได้รับมอบหมายให้ดูแลชั้นเรียนใดๆ กรุณาติดต่อผู้ดูแลระบบ
                      </div>
                    ) : (
                      <div className="row">
                        <div className="col-md-12 mb-3">
                          <div className="btn-group">
                            <button
                              type="button"
                              className={`btn ${formMode === 'class' ? 'btn-orange' : 'btn-default'}`}
                              onClick={() => handleChangeFormMode('class')}
                            >
                              <i className="fas fa-users mr-1"></i> บันทึกคะแนนทั้งชั้น
                            </button>
                            <button
                              type="button"
                              className={`btn ${formMode === 'student' ? 'btn-orange' : 'btn-default'}`}
                              onClick={() => handleChangeFormMode('student')}
                            >
                              <i className="fas fa-user mr-1"></i> บันทึกคะแนนรายบุคคล
                            </button>
                          </div>
                        </div>
                        
                        <div className="col-md-3">
                          <div className="form-group">
                            <label>ชั้นเรียน</label>
                            <select
                              className="form-control"
                              value={selectedClassId}
                              onChange={(e) => setSelectedClassId(e.target.value)}
                            >
                              {classes.map(classItem => (
                                <option key={classItem.id} value={classItem.id}>
                                  {classItem.class_name} (ห้อง {classItem.room_number})
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        
                        {formMode === 'student' && (
                          <div className="col-md-3">
                            <div className="form-group">
                              <label>นักเรียน</label>
                              <select
                                className="form-control"
                                value={selectedStudentId}
                                onChange={(e) => setSelectedStudentId(e.target.value)}
                              >
                                {students.map(student => (
                                  <option key={student.id} value={student.id}>
                                    {student.first_name} {student.last_name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        )}
                        
                        <div className="col-md-3">
                          <div className="form-group">
                            <label>วิชา</label>
                            <select
                              className="form-control"
                              value={selectedSubjectId}
                              onChange={(e) => setSelectedSubjectId(e.target.value)}
                            >
                              {subjects.map(subject => (
                                <option key={subject.id} value={subject.id}>
                                  {subject.subject_code} - {subject.subject_name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        
                        {formMode === 'class' && (
                          <div className="col-md-3">
                            <div className="form-group">
                              <label>ตัวชี้วัด</label>
                              <select
                                className="form-control"
                                value={selectedIndicatorId}
                                onChange={(e) => setSelectedIndicatorId(e.target.value)}
                                disabled={indicators.length === 0}
                              >
                                {indicators.length === 0 ? (
                                  <option value="">-- ไม่พบตัวชี้วัด --</option>
                                ) : (
                                  indicators.map(indicator => (
                                    <option key={indicator.id} value={indicator.id}>
                                      {indicator.indicator_code} - {indicator.indicator_name}
                                    </option>
                                  ))
                                )}
                              </select>
                              {indicators.length === 0 && (
                                <small className="form-text text-muted">
                                  กรุณาติดต่อผู้ดูแลระบบเพื่อเพิ่มตัวชี้วัดสำหรับวิชานี้
                                </small>
                              )}
                            </div>
                          </div>
                        )}
                        
                        <div className="col-md-12 mt-2">
                          <button
                            className="btn btn-orange"
                            onClick={formMode === 'class' ? fetchScoresByClassAndIndicator : fetchScoresByStudentAndSubject}
                            disabled={(formMode === 'class' && !selectedIndicatorId) || indicators.length === 0}
                          >
                            <i className="fas fa-search mr-1"></i> แสดงข้อมูล
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* แสดงคะแนน */}
                {(formMode === 'class' ? scores.length > 0 || students.length > 0 : students.length > 0 && indicators.length > 0) && (
                  <div className="row">
                    <div className="col-md-8">
                      <div className="card card-orange">
                        <div className="card-header">
                          <h3 className="card-title">
                            {formMode === 'class' ? (
                              <>
                                บันทึกคะแนนทั้งชั้น: {
                                  indicators.find(i => i.id === parseInt(selectedIndicatorId))?.indicator_name || 'ตัวชี้วัด'
                                }
                              </>
                            ) : (
                              <>
                                บันทึกคะแนนนักเรียน: {
                                  students.find(s => s.id === parseInt(selectedStudentId))?.first_name || ''
                                } {
                                  students.find(s => s.id === parseInt(selectedStudentId))?.last_name || ''
                                }
                              </>
                            )}
                          </h3>
                          <div className="card-tools">
                            <button
                              className="btn btn-tool"
                              onClick={handleSaveAllScores}
                              disabled={isLoading}
                            >
                              <i className="fas fa-save"></i> บันทึกทั้งหมด
                            </button>
                          </div>
                        </div>
                        <div className="card-body">
                          <div className="table-responsive">
                            <table className="table table-bordered table-striped">
                              <thead>
                                <tr>
                                  {formMode === 'class' ? (
                                    <>
                                      <th style={{ width: '40px' }}>ลำดับ</th>
                                      <th style={{ width: '60px' }}>รูปภาพ</th>
                                      <th>ชื่อ-นามสกุล</th>
                                      <th style={{ width: '100px' }}>คะแนน</th>
                                      <th style={{ width: '150px' }}>หมายเหตุ</th>
                                      <th style={{ width: '80px' }}>บันทึก</th>
                                    </>
                                  ) : (
                                    <>
                                      <th style={{ width: '40px' }}>ลำดับ</th>
                                      <th>รหัสตัวชี้วัด</th>
                                      <th>ชื่อตัวชี้วัด</th>
                                      <th style={{ width: '100px' }}>คะแนนเต็ม</th>
                                      <th style={{ width: '100px' }}>คะแนน</th>
                                      <th style={{ width: '150px' }}>หมายเหตุ</th>
                                      <th style={{ width: '80px' }}>บันทึก</th>
                                    </>
                                  )}
                                </tr>
                              </thead>
                              <tbody>
                                {formMode === 'class' ? (
                                  students.length === 0 ? (
                                    <tr>
                                      <td colSpan="6" className="text-center">
                                        ไม่พบข้อมูลนักเรียนในชั้นเรียนนี้
                                      </td>
                                    </tr>
                                  ) : (
                                    students.map((student, index) => (
                                      <tr key={student.id}>
                                        <td className="text-center">{index + 1}</td>
                                        <td>
                                          <img
                                            src={`https://cms2568.backend.kruwirat.com/uploads/${student.profile_pic}`}
                                            alt={`${student.first_name} ${student.last_name}`}
                                            className="student-profile-pic-sm"
                                            style={{ width: '40px', height: '40px' }}
                                            onError={(e) => {
                                              e.target.onerror = null;
                                              e.target.src = DEFAULT_AVATAR;
                                            }}
                                          />
                                        </td>
                                        <td>
                                          {student.first_name} {student.last_name}
                                          <small className="d-block text-muted">รหัส: {student.student_id}</small>
                                        </td>
                                        <td>
                                          <input
                                            type="number"
                                            className="form-control"
                                            value={editingScores[student.id] || ''}
                                            onChange={(e) => handleChangeScore(student.id, e.target.value)}
                                            step="0.1"
                                            min="0"
                                            max={indicators.find(i => i.id === parseInt(selectedIndicatorId))?.full_score || 100}
                                          />
                                        </td>
                                        <td>
                                          <input
                                            type="text"
                                            className="form-control"
                                            value={editingNotes[student.id] || ''}
                                            onChange={(e) => handleChangeNote(student.id, e.target.value)}
                                            placeholder="หมายเหตุ"
                                          />
                                        </td>
                                        <td>
                                          <button
                                            className="btn btn-success btn-sm"
                                            onClick={() => handleSaveScore(student)}
                                            disabled={editingScores[student.id] === undefined || editingScores[student.id] === ''}
                                          >
                                            <i className="fas fa-save"></i>
                                          </button>
                                        </td>
                                      </tr>
                                    ))
                                  )
                                ) : (
                                  indicators.length === 0 ? (
                                    <tr>
                                      <td colSpan="7" className="text-center">
                                        ไม่พบข้อมูลตัวชี้วัดสำหรับวิชานี้
                                      </td>
                                    </tr>
                                  ) : (
                                    indicators.map((indicator, index) => (
                                      <tr key={indicator.id}>
                                        <td className="text-center">{index + 1}</td>
                                        <td>{indicator.indicator_code}</td>
                                        <td>{indicator.indicator_name}</td>
                                        <td className="text-center">{indicator.full_score}</td>
                                        <td>
                                          <input
                                            type="number"
                                            className="form-control"
                                            value={editingScores[indicator.id] || ''}
                                            onChange={(e) => handleChangeScore(indicator.id, e.target.value)}
                                            step="0.1"
                                            min="0"
                                            max={indicator.full_score}
                                          />
                                        </td>
                                        <td>
                                          <input
                                            type="text"
                                            className="form-control"
                                            value={editingNotes[indicator.id] || ''}
                                            onChange={(e) => handleChangeNote(indicator.id, e.target.value)}
                                            placeholder="หมายเหตุ"
                                          />
                                        </td>
                                        <td>
                                          <button
                                            className="btn btn-success btn-sm"
                                            onClick={() => handleSaveScore(indicator)}
                                            disabled={editingScores[indicator.id] === undefined || editingScores[indicator.id] === ''}
                                          >
                                            <i className="fas fa-save"></i>
                                          </button>
                                        </td>
                                      </tr>
                                    ))
                                  )
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="col-md-4">
                      <div className="card card-orange">
                        <div className="card-header">
                          <h3 className="card-title">แผนภูมิคะแนน</h3>
                        </div>
                        <div className="card-body">
                          <canvas id="scoreChart"></canvas>
                        </div>
                      </div>
                      
                      <div className="card card-orange">
                        <div className="card-header">
                          <h3 className="card-title">ข้อมูลตัวชี้วัด</h3>
                        </div>
                        <div className="card-body">
                          {formMode === 'class' ? (
                            <div>
                              <p><strong>รหัสตัวชี้วัด:</strong> {indicators.find(i => i.id === parseInt(selectedIndicatorId))?.indicator_code || '-'}</p>
                              <p><strong>ชื่อตัวชี้วัด:</strong> {indicators.find(i => i.id === parseInt(selectedIndicatorId))?.indicator_name || '-'}</p>
                              <p><strong>คะแนนเต็ม:</strong> {indicators.find(i => i.id === parseInt(selectedIndicatorId))?.full_score || '-'}</p>
                              <p><strong>น้ำหนัก:</strong> {indicators.find(i => i.id === parseInt(selectedIndicatorId))?.weight || '-'}</p>
                              <p><strong>คำอธิบาย:</strong> {indicators.find(i => i.id === parseInt(selectedIndicatorId))?.description || '-'}</p>
                            </div>
                          ) : (
                            <div>
                              <p><strong>รหัสนักเรียน:</strong> {students.find(s => s.id === parseInt(selectedStudentId))?.student_id || '-'}</p>
                              <p><strong>ชื่อ-นามสกุล:</strong> {students.find(s => s.id === parseInt(selectedStudentId))?.first_name || '-'} {students.find(s => s.id === parseInt(selectedStudentId))?.last_name || '-'}</p>
                              <p><strong>วิชา:</strong> {subjects.find(s => s.id === parseInt(selectedSubjectId))?.subject_name || '-'}</p>
                              <p><strong>จำนวนตัวชี้วัด:</strong> {indicators.length}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </div>
      
      <Footer />
    </div>
  );
}

export default TeacherScores;